// When a matter needs an extension or a new grant applied for, and by when.
//
// Staff were missing extensions and new grants because nothing told them one was due: the
// diaries record a funding status and the Aid Planner works out "extension review", but each
// only says so on the row itself, and only to someone already looking at it. This reads what
// those tools already hold, works out an apply-by date from the listing date, and hands back
// one list the VALS Diary, the LACW billing sheet (and LACW's view-only Aid Status Diary)
// and the Aid Planner all show at the top.
//
// Nothing here is new data entry. A reminder clears when the funding status changes, or when
// someone presses "Mark as applied", which sets grantActionLodged on the entry itself so it
// travels with the week wherever that week is saved.

import { STAGES_COVERED_BY_GRANT, stageForDiaryCodes } from "./diaryCodes.js";
import { assessAidPlanner } from "./aidPlannerRules.js";
import { exclusionFor } from "./approvalLetterGrants.js";

// VLA will not pay for work done before the grant or extension covers it, and a late
// application can force an adjournment. Two weeks before the listing gives VLA time to
// decide; inside a week it is urgent.
export const APPLY_LEAD_DAYS = 14;
export const URGENT_DAYS = 7;
// An old week left open should not keep shouting about listings long gone.
export const STALE_AFTER_DAYS = 28;

export const REMINDER_KINDS = {
  extension: "Extension required",
  new_grant: "New grant required",
  extension_check: "Check if extension needed",
  follow_up: "Chase VLA decision",
  not_covered: "Not covered by the grant",
  condition: "Grant condition outstanding",
};

export const URGENCY_ORDER = { overdue: 0, urgent: 1, due: 2, upcoming: 3 };

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

// Dates are handled as UTC midnights so a daylight-saving change never moves a day.
function utcDay(year, monthIndex, day) {
  const date = new Date(Date.UTC(year, monthIndex, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === monthIndex && date.getUTCDate() === day
    ? date
    : null;
}

function todayUtc(today = new Date()) {
  return utcDay(today.getFullYear(), today.getMonth(), today.getDate());
}

export function isoDay(date) {
  return date ? date.toISOString().slice(0, 10) : "";
}

export function formatReminderDate(value) {
  if (!value) return "";
  const [year, month, day] = String(value).split("-").map(Number);
  const date = utcDay(year, month - 1, day);
  if (!date) return String(value);
  return new Intl.DateTimeFormat("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(date);
}

// A listing date as the diaries and planner hold it: "21/07/2026", "2026-07-21", or the VALS
// diary's "Monday 20", which has lost its month. For that one, take the nearest date to today
// that falls on that weekday and day of the month; the pair repeats only every few months.
export function resolveListingDate(value, today = new Date()) {
  const text = String(value || "").trim();
  if (!text) return null;

  let match = text.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (match) {
    const year = Number(match[3]) < 100 ? 2000 + Number(match[3]) : Number(match[3]);
    return utcDay(year, Number(match[2]) - 1, Number(match[1]));
  }
  match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (match) return utcDay(Number(match[1]), Number(match[2]) - 1, Number(match[3]));

  match = text.match(/^([a-z]+)\s+(\d{1,2})$/i);
  if (match) {
    const weekday = WEEKDAYS.indexOf(match[1].toLowerCase());
    const dayOfMonth = Number(match[2]);
    const base = todayUtc(today);
    if (weekday < 0 || !base) return null;
    let best = null;
    for (let offset = -6; offset <= 6; offset += 1) {
      const candidate = utcDay(base.getUTCFullYear(), base.getUTCMonth() + offset, dayOfMonth);
      if (!candidate || candidate.getUTCDay() !== weekday) continue;
      if (!best || Math.abs(candidate - base) < Math.abs(best - base)) best = candidate;
    }
    return best;
  }
  return null;
}

// Listings that usually sit outside the grant a summary matter starts with. A "check", not a
// finding: the grant letter decides, and the Aid Extension Flowchart walks through it.
// The diaries use the universal diary codes (diaryCodes.js): CMEN a contest mention, CONT a
// contested hearing, COMM / CCON a committal, TRIAL, APP an appeal. Listings written out in
// words are still read; a firm's own abbreviations only through the codes' old-code list.
const STAGE_CHANGE_PATTERNS = [
  [/\bappeal\b/i, "Appeal"],
  [/\bspecial hearing\b/i, "Special hearing"],
  [/\bcontest(?:ed)?\s+mention\b/i, "Contest mention"],
  [/\bcontest(?:ed)?\b|\bsummary hearing\b|^hearing$/i, "Contested hearing"],
  [/\bcommittal\s+mention\b/i, "Committal mention"],
  [/\bcommittal\b/i, "Committal"],
  [/\btrial\b/i, "Trial"],
];

export function stageChangeFor(appearanceType = "") {
  const text = String(appearanceType || "").trim();
  if (!text) return "";
  const fromCodes = stageForDiaryCodes(text);
  if (fromCodes) return fromCodes;
  const hit = STAGE_CHANGE_PATTERNS.find(([pattern]) => pattern.test(text));
  return hit ? hit[1] : "";
}

// The stage a listing belongs to, for extensions. An approved extension covers the rest of
// the stage it was sought for: every later listing in that stage, and none in the next one.
// A contest mention is a stage of its own, apart from the contest. Listings with no named
// stage (mentions, pleas, sentence) form one stage of their own.
export function listingStage(appearanceType = "") {
  if (!String(appearanceType || "").trim()) return "";
  return stageChangeFor(appearanceType) || "Other listings";
}

// Whether an approved extension covers the matter's next listing: same stage as the listing
// it was sought for. Approvals recorded before stages were kept match the listing date.
export function extensionCoversListing(planner) {
  const funding = planner?.funding || {};
  if (!/approved|granted/i.test(funding.extensionDecision || "")) return false;
  if (funding.extensionApprovedForStage) {
    return funding.extensionApprovedForStage === listingStage(planner.matter?.appearanceType);
  }
  return Boolean(funding.extensionApprovedForListing)
    && funding.extensionApprovedForListing === planner.matter?.nextAppearanceDate;
}

function timing(listingDate, today) {
  const base = todayUtc(today);
  if (!listingDate || !base) {
    return { listingDate: "", applyBy: "", daysToListing: null, urgency: "due" };
  }
  const applyBy = new Date(listingDate.getTime() - APPLY_LEAD_DAYS * DAY_MS);
  const daysToListing = Math.round((listingDate - base) / DAY_MS);
  const urgency = daysToListing < 0
    ? "overdue"
    : daysToListing <= URGENT_DAYS
      ? "urgent"
      : applyBy <= base
        ? "due"
        : "upcoming";
  return { listingDate: isoDay(listingDate), applyBy: isoDay(applyBy), daysToListing, urgency };
}

const STAGE_REASONS = {
  Appeal: "Listed for an appeal. An appeal needs its own grant.",
  "Contested hearing": "Adjourned to a contested hearing. Under a consolidated grant a new grant is needed for the contest; check what the grant covers.",
  Committal: "Adjourned to a committal or contested committal. An extension is needed.",
};

// One diary row, from either diary. Returns null when nothing needs applying for.
export function assessDiaryEntry(entry, { source = "diary", today = new Date() } = {}) {
  if (!entry || !String(entry.clientName || "").trim()) return null;
  if (entry.grantActionLodged) return null;

  const status = String(entry.aidStatus || "").trim();
  const stageChange = stageChangeFor(entry.appearanceType);
  let kind = "";
  let reason = "";

  if (status === "Extension required") {
    kind = "extension";
    // Filled from the Aid Planner, the diary can say why.
    reason = entry.aidPlannerFill?.status === "Extension required" && entry.aidPlannerFill.reason
      ? entry.aidPlannerFill.reason
      : "The diary marks this matter as needing an extension.";
  } else if (status === "Aid not applied for") {
    if (entry.aidApplicationOnFile) return null;
    kind = "new_grant";
    reason = "No grant is in place and no application is recorded.";
  } else if ((status === "Aid in place" || status === "Aid confirmed") && stageChange && !STAGES_COVERED_BY_GRANT.has(stageChange)) {
    // Only a matter adjourned off to a stage the grant does not reach: a contested hearing, a
    // committal or contested committal, a trial, a special hearing or an appeal. A contest
    // mention or committal mention is covered (Emma, 22 Oct 2026).
    kind = stageChange === "Appeal" ? "new_grant" : "extension_check";
    reason = STAGE_REASONS[stageChange] || `Listed for a ${stageChange.toLowerCase()}, which the current grant may not cover.`;
  } else {
    return null;
  }

  const listing = resolveListingDate(entry.date, today);
  return {
    id: `${source}:${entry.id}`,
    entryId: entry.id,
    source,
    kind,
    label: REMINDER_KINDS[kind],
    reason,
    clientName: String(entry.clientName).replace(/^\*+/, "").trim(),
    court: entry.court || "",
    appearanceType: entry.appearanceType || "",
    lawyer: entry.solicitor || entry.lawyer || "",
    aidNumber: entry.aidNumber || "",
    ...timing(listing, today),
  };
}

// Which of the grant's fee rows a listing uses up. The planner's listing types are broad
// ("Plea", "Sentence"); the fee rows use VLA's claim wording ("CC PLEA APPEARANCE DAY 1",
// "APPEARANCE" with the alias "PLEA").
const LISTING_FEE_WORDS = {
  "Contested Hearing": { any: ["DEFEND/CONTEST", "CONTESTED HEARING"] },
  "Contest Mention": { any: ["CONTEST MENTION", "CONT MENTION"], none: ["PLEA"] },
  Plea: { any: ["PLEA"], none: ["CONTEST MENTION", "PREPARATION"] },
  Sentence: { any: ["SENTENCE"], none: ["INDICATION"] },
  "Bail Application": { any: ["BAIL APPEARANCE", "BAIL APPLICATION"] },
  Committal: { any: ["COMMITTAL MENTION", "COMMITTAL CONTEST"] },
  Trial: { any: ["TRIAL APPEARANCE", "TRIAL SUBSEQUENT"] },
  Appeal: { any: ["APPEAL APPEARANCE", "APPEAL BRIEF", "SENTENCE BRIEF", "CONVICTION BRIEF"] },
};

function feeRowWords(row) {
  return [row.appearanceType, row.feeType, ...(row.trackerAliases || [])].map((value) => String(value || "").toUpperCase());
}

// Consolidated-rate rows only count under a consolidated grant, and the single rates only
// under a single one, when the grant has both kinds of row.
const feeKey = (value) => String(value || "").toUpperCase().replace(/[^A-Z0-9]+/g, "");

export function feeRowsForListing(appearanceType, availability = [], { consolidated = false } = {}) {
  // Once the grant is known, the next listing is picked from the grant's own fee rows
  // ("DEFEND/CONTEST DAILY FEE", "CONTEST MENTION"), so it names its row outright.
  const key = feeKey(appearanceType);
  const exact = key ? availability.filter((row) => feeRowWords(row).map(feeKey).includes(key)) : [];
  if (exact.length > 0) return exact;
  const words = LISTING_FEE_WORDS[appearanceType];
  if (!words) return null;
  const isConsolRow = (row) => feeRowWords(row).some((text) => text.includes("CONSOL"));
  const hasBoth = availability.some(isConsolRow) && availability.some((row) => !isConsolRow(row));
  return availability.filter((row) => {
    if (hasBoth && isConsolRow(row) !== consolidated) return false;
    const texts = feeRowWords(row);
    return texts.some((text) => words.any.some((word) => text.includes(word)))
      && !texts.some((text) => (words.none || []).some((word) => text.includes(word)));
  });
}

// What the approved grant allows against what has been claimed, for the next listing.
function allowanceCheck(planner, availability) {
  const listing = planner.matter?.appearanceType || "";
  if (!planner.funding?.grantInPlace && !(planner.aid?.approvedGrants || []).length) return null;
  if (!Array.isArray(availability) || availability.length === 0) return null;
  const aidTypes = [...(planner.aid?.aidTypeList || []), ...(planner.aid?.approvedGrants || []).map((grant) => grant.aidType)];
  const rows = feeRowsForListing(listing, availability, { consolidated: aidTypes.some((type) => /consol/i.test(type || "")) });
  if (rows === null) return null;
  const grants = (planner.aid?.approvedGrants || []).map((grant) => `Table ${grant.feeTable}`);
  const grantName = grants.length ? `the approved grant (${[...new Set(grants)].join(", ")})` : "the approved grant";
  if (rows.length === 0) {
    return {
      kind: "extension",
      detail: `none:${listing}`,
      reason: `The matter is listed for a ${listing.toLowerCase()}, and ${grantName} has no fee for it.`,
    };
  }
  const allowed = rows.reduce((sum, row) => sum + (Number(row.allocationCount) || 0), 0);
  const remaining = rows.reduce((sum, row) => sum + (Number(row.remaining) || 0), 0);
  const name = rows[0].feeType.toLowerCase();
  // "contest mention" -> "contest mention fees"; "defend/contest daily fee" -> "... daily fees"
  const plural = /\bfee$/.test(name) ? `${name}s` : `${name} fees`;
  if (remaining <= 0) {
    return {
      kind: "extension",
      detail: `used:${rows.map((row) => row.feeType).join("+")}:${allowed}`,
      reason: `All ${allowed} ${allowed === 1 ? plural.replace(/s$/, "") : plural} under ${grantName} ${allowed === 1 ? "has" : "have"} been claimed, and the matter is listed for another.`,
    };
  }
  if (remaining === 1 && allowed > 1) {
    return {
      kind: "extension_check",
      detail: `last:${rows.map((row) => row.feeType).join("+")}:${allowed}`,
      reason: `Only 1 of ${allowed} ${plural} is left under ${grantName}. This listing uses it; apply before the one after.`,
    };
  }
  return null;
}

function plannerReminder(planner, today, { kind, reason, detail = "", doneLabel }) {
  const listing = resolveListingDate(planner.matter?.nextAppearanceDate, today);
  const timed = timing(listing, today);
  return {
    id: `planner:${planner.matterId}:${kind}:${detail}`,
    key: `${kind}:${detail}:${timed.listingDate}`,
    entryId: planner.matterId,
    source: "planner",
    kind,
    label: REMINDER_KINDS[kind],
    reason,
    doneLabel,
    clientName: planner.client?.fullName || [planner.client?.firstName, planner.client?.lastName].filter(Boolean).join(" ") || "Unnamed client",
    court: planner.matter?.court || "",
    appearanceType: planner.matter?.appearanceType || "",
    lawyer: planner.matter?.lawyer || "",
    aidNumber: planner.aid?.aidNumber || "",
    ...timed,
  };
}

// Every reminder for one Aid Planner matter. The planner's own assessment comes first, so the
// two never disagree; then the approval letter's allowances, exclusions and conditions.
// availabilityFor(planner) gives the grant's fee rows with what is claimed and remaining
// (getClaimAvailabilityTable); it is passed in so this file stays free of the fee tables.
export function plannerMatterReminders(planner, { today = new Date(), availabilityFor } = {}) {
  if (!planner?.matterId) return [];
  if (planner.finalisation?.matterFinalised || planner.finalisation?.grantClosed) return [];

  const guidance = assessAidPlanner(planner);
  const funding = planner.funding || {};
  const found = [];

  // Once an extension has been requested, the job is chasing VLA's decision, not applying
  // again, even though the fees are still used up.
  const awaitingDecision = funding.extensionRequested && !funding.extensionDecision;
  const coveredByExtension = extensionCoversListing(planner);
  const stageNotCovered = (guidance.reasons || []).some((text) => /not appear to be covered by the current stage/.test(text));

  if (guidance.status === "extension_review" && !(coveredByExtension && stageNotCovered)) {
    let kind = /new grant/i.test(`${guidance.nextAction} ${(guidance.reasons || []).join(" ")}`) ? "new_grant" : "extension";
    let reason = guidance.nextAction || "";
    if (funding.extensionRequested && !funding.extensionDecision) {
      kind = "follow_up";
      reason = "An extension has been requested but no decision is recorded.";
    }
    found.push({ kind, reason, detail: "review" });
  } else if (guidance.status === "apply_now") {
    found.push({ kind: "new_grant", reason: guidance.nextAction || "", detail: "apply" });
  } else {
    const allowance = allowanceCheck(planner, availabilityFor ? availabilityFor(planner) : []);
    const exclusion = exclusionFor(planner.matter?.appearanceType, planner.aid?.grantExclusions);
    // The fees counted are the original grant's: the portal doesn't know how many the
    // extension added, so within its stage neither "used" nor "last one left" applies.
    const coveredAllowance = allowance && (
      (awaitingDecision && allowance.kind === "extension")
      || (coveredByExtension && (allowance.kind === "extension" || allowance.kind === "extension_check"))
    );
    if (allowance && !coveredAllowance) {
      found.push(allowance);
    } else if (awaitingDecision) {
      found.push({ kind: "follow_up", detail: "decision", reason: "An extension has been requested but no decision is recorded." });
    } else if (exclusion) {
      found.push({
        kind: "not_covered",
        detail: `excluded:${planner.matter?.appearanceType}`,
        reason: `The approval letter says the grant will not cover ${exclusion}, save for exceptional circumstances. Check whether a special mention fee applies, or whether the appearance needs funding another way.`,
      });
    } else if (planner.application?.decisionResult === "Refused") {
      found.push({ kind: "new_grant", detail: "refused", reason: "The grant was refused. Decide whether to reapply or seek review." });
    }
  }

  for (const condition of planner.aid?.grantConditions || []) {
    if (condition.kind === "proof_of_means") {
      found.push({ kind: "condition", detail: "proof_of_means", reason: condition.text, doneLabel: "Proof of means on file" });
    }
  }

  const done = planner.aid?.doneReminders || {};
  return found
    .map((item) => plannerReminder(planner, today, item))
    .filter((reminder) => !done[reminder.key]);
}

// The most pressing reminder for one matter.
export function assessPlannerMatter(planner, options = {}) {
  return plannerMatterReminders(planner, options)[0] || null;
}

export function sortReminders(reminders = []) {
  return reminders
    .filter((reminder) => reminder && !(reminder.daysToListing !== null && reminder.daysToListing < -STALE_AFTER_DAYS))
    .sort((a, b) =>
      (URGENCY_ORDER[a.urgency] ?? 9) - (URGENCY_ORDER[b.urgency] ?? 9)
      || (a.listingDate || "9999").localeCompare(b.listingDate || "9999")
      || a.clientName.localeCompare(b.clientName));
}

export function diaryReminders(entries = [], options = {}) {
  return sortReminders((Array.isArray(entries) ? entries : []).map((entry) => assessDiaryEntry(entry, options)));
}

export function plannerReminders(matters = [], options = {}) {
  return sortReminders((Array.isArray(matters) ? matters : []).flatMap((matter) => plannerMatterReminders(matter, options)));
}

export function describeTiming(reminder) {
  if (!reminder?.listingDate) return "Listing date unknown — apply now";
  const days = reminder.daysToListing;
  if (reminder.urgency === "overdue") return `Listing was ${formatReminderDate(reminder.listingDate)} — apply now (late application)`;
  if (days === 0) return "Listed today — apply now";
  if (reminder.urgency === "urgent") return `Listed in ${days} day${days === 1 ? "" : "s"} — apply now`;
  if (reminder.urgency === "due") return `Apply now — listed ${formatReminderDate(reminder.listingDate)}`;
  return `Apply by ${formatReminderDate(reminder.applyBy)}`;
}

function icsText(value) {
  return String(value || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
}

// An all-day Outlook entry on the apply-by date, with a 9am pop-up, so the reminder reaches
// the lawyer's own calendar and not only this screen. A reminder already past its apply-by
// date is put on today.
export function buildReminderIcs(reminder, { today = new Date() } = {}) {
  const todayIso = isoDay(todayUtc(today));
  const day = (reminder.applyBy && reminder.applyBy > todayIso ? reminder.applyBy : todayIso).replaceAll("-", "");
  const summary = `${reminder.label}: ${reminder.clientName}`;
  const description = [
    reminder.reason,
    reminder.court && `Court: ${reminder.court}`,
    reminder.appearanceType && `Listed for: ${reminder.appearanceType}`,
    reminder.listingDate && `Listing date: ${formatReminderDate(reminder.listingDate)}`,
    reminder.aidNumber && `Aid number: ${reminder.aidNumber}`,
    "Lodge in ATLAS before the work is done.",
  ].filter(Boolean).join("\n");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//CLS//Grant reminders//EN",
    "BEGIN:VEVENT",
    `UID:${String(reminder.id).replace(/[^a-z0-9:-]/gi, "")}-${day}@grant-reminders`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").split(".")[0]}Z`,
    `DTSTART;VALUE=DATE:${day}`,
    `SUMMARY:${icsText(summary)}`,
    `DESCRIPTION:${icsText(description)}`,
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `DESCRIPTION:${icsText(summary)}`,
    "TRIGGER:PT9H",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}
